import { useCallback, useMemo } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

import {
  Box,
  Tab,
  Tabs,
  Typography,
} from "@braintrust/braintrust-ui-components";
import { JobMessenger } from "@js/apps/messenger/messengers/jobs";
import type { EmployerBidListBid, Job } from "@js/types/jobs";

import { JobBidResumeViewer } from "../../bid-resume-viewer";

import { BidDetailsApplicationTab } from "./bid-details-application-tab";
import { BidInterviewTab } from "./bid-interview-tab";

const TABS = {
  APPLICATION: {
    label: "Application",
    param: "application",
    component: BidDetailsApplicationTab,
  },
  INTERVIEW: {
    label: "Interview",
    param: "interview",
    component: BidInterviewTab,
  },
  RESUME: {
    label: "Resume",
    param: "resume",
    component: JobBidResumeViewer,
  },
  MESSAGES: {
    label: "Messages",
    param: "messages",
    component: JobMessenger,
  },
} as const;

type TabData = ObjectValues<typeof TABS>;

export const BidTabs = ({
  bid,
  job,
}: {
  bid: EmployerBidListBid;
  job: Job;
}) => {
  const [params, setParams] = useSearchParams();
  const { state } = useLocation();
  const currentTabParam = params.get("tab");

  const updateTabParam = useCallback(
    (tabParam: string) => {
      const current = params.get("tab");
      if (current === tabParam) {
        return;
      }
      params.delete("tab");
      const prevParams = params.toString();

      const newParams = !!prevParams.length
        ? `?${prevParams}&tab=${tabParam}`
        : `?tab=${tabParam}`;

      setParams(newParams, {
        replace: true,
        state: {
          ...state,
          noScroll: true,
          disableRemountIfTheSameLocation: true,
        },
      });
    },
    [params, setParams, state],
  );

  const availableTabs = useMemo(() => {
    const tabs: TabData[] = [
      TABS.APPLICATION,
      !!bid.interview?.is_completed ? TABS.INTERVIEW : null,
      TABS.RESUME,
      !!bid?.freelancer.user.can_be_messaged ? TABS.MESSAGES : null,
    ].filter((tabData): tabData is TabData => Boolean(tabData));

    return tabs;
  }, [bid]);

  const tab = useMemo(() => {
    const defaultParam = availableTabs[0].param;
    if (!currentTabParam) {
      return defaultParam;
    }

    const isValidTab = availableTabs.some(
      (availableTab) => availableTab.param === currentTabParam,
    );
    if (!isValidTab) {
      return defaultParam;
    }

    return currentTabParam;
  }, [currentTabParam, availableTabs]);

  const currentTab = useMemo(
    () => availableTabs.find((i) => i.param === tab),
    [tab, availableTabs],
  );

  return (
    <Box>
      <Tabs
        aria-label="refer-and-earn-tabs"
        value={tab}
        variant="scrollable"
        scrollButtons={false}
      >
        {availableTabs.map(({ param, label }) => {
          return (
            <Tab
              key={label}
              value={param}
              onClick={() => updateTabParam(param)}
              label={
                <Typography component="span" variant="label" size="medium">
                  {label}
                </Typography>
              }
            />
          );
        })}
      </Tabs>
      <Box sx={{ mt: 3 }}>
        {currentTab?.component ? (
          <currentTab.component bid={bid} job={job} />
        ) : (
          currentTab?.label
        )}
      </Box>
    </Box>
  );
};
